import { spanFromRegExpMatch } from '../spans';
import type { ExtractorOutput } from './types';
import type { MatchSpan, ParsedSearchFilters } from '../types';

function toIsoDate(date: Date): string {
  return date.toISOString().split('T')[0];
}

function daysAgo(now: Date, days: number): string {
  const d = new Date(now.getTime());
  d.setUTCDate(d.getUTCDate() - days);
  return toIsoDate(d);
}

/** First / last day of the calendar month before `now` (UTC). */
function previousCalendarMonthRange(now: Date): { listedSince: string; listedTo: string } {
  const firstOfThisMonth = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
  const lastOfPrev = new Date(firstOfThisMonth.getTime());
  lastOfPrev.setUTCDate(0);
  const firstOfPrev = new Date(Date.UTC(lastOfPrev.getUTCFullYear(), lastOfPrev.getUTCMonth(), 1));
  return { listedSince: toIsoDate(firstOfPrev), listedTo: toIsoDate(lastOfPrev) };
}

function pushDateField(
  partial: Partial<ParsedSearchFilters>,
  spans: MatchSpan[],
  query: string,
  match: RegExpMatchArray,
  field: 'listedSince' | 'listedTo',
  value: string,
  ruleId: string,
  confidence: number
): void {
  partial[field] = value;
  spans.push(
    spanFromRegExpMatch(query, match, field, value, {
      confidence,
      ruleId,
    })
  );
}

/**
 * Active Date filter phrases → `listedSince` (Since) and/or `listedTo` (Until).
 * Gated by the `relativeDate` pipeline flag.
 */
export function extractListedSince(query: string, now: Date = new Date()): ExtractorOutput {
  const partial: Partial<ParsedSearchFilters> = {};
  const spans: MatchSpan[] = [];

  // Closed range: previous calendar month → both Since and Until.
  const lastMonthMatch = /\blisted\s+last\s+month\b/gi.exec(query);
  if (lastMonthMatch) {
    const { listedSince, listedTo } = previousCalendarMonthRange(now);
    pushDateField(partial, spans, query, lastMonthMatch, 'listedSince', listedSince, 'listedSince.last-month', 0.9);
    // Same span also accounts for listedTo intent (one phrase → both bounds).
    partial.listedTo = listedTo;
    spans.push({
      start: lastMonthMatch.index ?? 0,
      end: (lastMonthMatch.index ?? 0) + lastMonthMatch[0].length,
      field: 'listedTo',
      raw: lastMonthMatch[0],
      value: listedTo,
      confidence: 0.9,
      ruleId: 'listedTo.last-month',
    });
    return { partial, spans };
  }

  // --- listedTo (Active Date Until) — require until/before/up to/older than ---
  const untilDays =
    /\b(?:until|before|up\s+to|older\s+than)\s+(?:the\s+)?(?:last|past)\s+(\d+)\s+days?\b|\bolder\s+than\s+(\d+)\s+days?\b/gi;
  const untilDaysMatch = untilDays.exec(query);
  if (untilDaysMatch) {
    const days = parseInt(untilDaysMatch[1] ?? untilDaysMatch[2], 10);
    if (Number.isFinite(days) && days > 0) {
      pushDateField(
        partial,
        spans,
        query,
        untilDaysMatch,
        'listedTo',
        daysAgo(now, days),
        'listedTo.days',
        0.9
      );
    }
  }

  if (partial.listedTo === undefined) {
    const untilYesterday = /\b(?:until|before|up\s+to)\s+yesterday\b/gi.exec(query);
    if (untilYesterday) {
      pushDateField(
        partial,
        spans,
        query,
        untilYesterday,
        'listedTo',
        daysAgo(now, 1),
        'listedTo.yesterday',
        0.9
      );
    }
  }

  if (partial.listedTo === undefined) {
    const untilWeek =
      /\b(?:until|before|up\s+to)\s+(?:last\s+week|this\s+week|the\s+past\s+week)\b/gi.exec(query);
    if (untilWeek) {
      pushDateField(
        partial,
        spans,
        query,
        untilWeek,
        'listedTo',
        daysAgo(now, 7),
        'listedTo.week',
        0.85
      );
    }
  }

  if (partial.listedTo === undefined) {
    const untilMonth =
      /\b(?:until|before|up\s+to)\s+(?:last\s+month|this\s+month|the\s+past\s+month)\b/gi.exec(query);
    if (untilMonth) {
      pushDateField(
        partial,
        spans,
        query,
        untilMonth,
        'listedTo',
        daysAgo(now, 30),
        'listedTo.month',
        0.85
      );
    }
  }

  // --- listedSince (Active Date Since) ---
  const numbered =
    /\b(?:listed\s+)?(?:in\s+)?(?:the\s+)?(?:last|past)\s+(\d+)\s+days?\b|\blisted\s+(?:within\s+)?(\d+)\s+days?\b/gi;
  const numberedMatch = numbered.exec(query);
  if (numberedMatch) {
    // Avoid treating "until/before/older than last N days" as a Since window.
    const matchedText = numberedMatch[0];
    const start = numberedMatch.index ?? 0;
    const prefix = query.slice(Math.max(0, start - 12), start).toLowerCase();
    const isUntilContext =
      /(?:until|before|up\s+to|older\s+than)\s*$/i.test(prefix) ||
      /^older\s+than\b/i.test(matchedText);
    if (!isUntilContext) {
      const days = parseInt(numberedMatch[1] ?? numberedMatch[2], 10);
      if (Number.isFinite(days) && days > 0) {
        pushDateField(
          partial,
          spans,
          query,
          numberedMatch,
          'listedSince',
          daysAgo(now, days),
          'listedSince.days',
          0.9
        );
      }
    }
  }

  if (partial.listedSince === undefined) {
    const weekMatch =
      /\b(?:listed\s+)?(?:this|in\s+the\s+last)\s+week\b|\blast\s+7\s+days\b/gi.exec(query);
    if (weekMatch) {
      const start = weekMatch.index ?? 0;
      const prefix = query.slice(Math.max(0, start - 12), start).toLowerCase();
      if (!/(?:until|before|up\s+to)\s*$/i.test(prefix)) {
        pushDateField(
          partial,
          spans,
          query,
          weekMatch,
          'listedSince',
          daysAgo(now, 7),
          'listedSince.week',
          0.85
        );
      }
    }
  }

  if (partial.listedSince === undefined) {
    const monthMatch =
      /\b(?:listed\s+)?(?:this|in\s+the\s+last)\s+month\b|\blast\s+30\s+days\b/gi.exec(query);
    if (monthMatch) {
      const start = monthMatch.index ?? 0;
      const prefix = query.slice(Math.max(0, start - 12), start).toLowerCase();
      if (!/(?:until|before|up\s+to)\s*$/i.test(prefix)) {
        pushDateField(
          partial,
          spans,
          query,
          monthMatch,
          'listedSince',
          daysAgo(now, 30),
          'listedSince.month',
          0.85
        );
      }
    }
  }

  if (partial.listedSince === undefined) {
    const newMatch = /\b(?:new\s+listings?|recently\s+listed|just\s+listed)\b/gi.exec(query);
    if (newMatch) {
      pushDateField(
        partial,
        spans,
        query,
        newMatch,
        'listedSince',
        daysAgo(now, 7),
        'listedSince.new-default-7d',
        0.8
      );
    }
  }

  return { partial, spans };
}
